import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { ContactWithAccess } from "@types"

// =====================
// Type definitions des réponses des services
// =====================

/**
 * Relation d'accès entre une entreprise, un bâtiment et des utilisateurs
 */
export type GetUpdateAccessResponse = {
    /**
     * Identifiant unique de la relation
     */
    _id: string
    /**
     * Identifiant de l'entreprise
     */
    _id_company: string
    /**
     * Identifiant du bâtiment
     */
    _id_building: string
    /**
     * Droits accordés (ex: "views")
     */
    rights: string
    /**
     * Liste des utilisateurs ayant accès
     */
    visible_to: string[]
    /**
     * Date de création
     */
    createdAt: string
    /**
     * Date de dernière mise à jour
     */
    updatedAt: string
}

/**
 * Réponse de l'API pour la récupération des contacts avec leurs accès
 */
export type GetContactsWithAccessResponse = {
    data: ContactWithAccess[]
    total: number
}

/**
 * Réponse de l'API pour l'ajout/suppression d'accès
 */
export type AccessFromBuildingsResponse = {
    /**
     * Identifiant du bâtiment
     */
    _id_building: string
    /**
     * Identifiant de l'utilisateur
     */
    _id_user: string
}

// =====================
// Service
// =====================

/**
 * Service pour gérer les accès des collaborateurs aux bâtiments
 */
export class AccessService {
    /**
     * Récupère la liste des contacts avec leurs accès pour un bâtiment
     * @param buildingId - Identifiant du bâtiment
     * @returns Promise avec tableau de contacts
     */
    async getContactsWithAccess(
        buildingId: string
    ): Promise<GetContactsWithAccessResponse> {
        return handleServiceCall<GetContactsWithAccessResponse>(async () => {
            const { data } = await axios.get(
                "/api/metier/v5/access/ressource",
                {
                    params: {
                        _id_building: buildingId,
                    },
                }
            )

            return {
                total: data.total ?? 0,
                data: data.data || [],
            }
        })
    }

    /**
     * Récupère la liste des contacts avec leurs accès pour un dossier
     * @param folderId - Identifiant du dossier
     */
    async getContactsWithAccessForFolder(
        folderId: string
    ): Promise<GetContactsWithAccessResponse> {
        return handleServiceCall<GetContactsWithAccessResponse>(async () => {
            const { data } = await axios.get(
                "/api/metier/v5/access/ressource",
                {
                    params: {
                        _id_folder: folderId,
                    },
                }
            )

            return {
                total: data.total ?? 0,
                data: data.data || [],
            }
        })
    }

    /**
     * Ajoute des accès à des utilisateurs pour un bâtiment
     * @param buildings - Tableau d'objets avec _id_building et _id_user
     * @returns Promise avec les relations d'accès mises à jour
     */
    async addAccessToBuildings(
        buildings: { _id_building: string; _id_user: string }[]
    ): Promise<GetUpdateAccessResponse[]> {
        return handleServiceCall<GetUpdateAccessResponse[]>(async () => {
            const { data } = await axios.put("/api/metier/building/access", {
                buildings,
            })
            return data
        })
    }

    /**
     * Supprime des accès d'utilisateurs pour un bâtiment
     * @param buildings - Tableau d'objets avec _id_building et _id_user
     * @returns Promise avec les relations d'accès mises à jour
     */
    async removeAccessFromBuildings(
        buildings: { _id_building: string; _id_user: string }[]
    ): Promise<GetUpdateAccessResponse[]> {
        return handleServiceCall<GetUpdateAccessResponse[]>(async () => {
            const { data } = await axios.delete("/api/metier/building/access", {
                data: { buildings },
            })
            return data
        })
    }
}
